// My Patients: the patients this nurse covers this shift — every bed in a ward
// they're rostered to for the shift in progress, plus any beds pinned to them
// for that shift (useMyCoverage). Roster is the default coverage; assignment is
// the optional override. Both expire with the shift.
// Search & location are pushed to the backend (ipd_admission/
// filters); the covered-bed set is applied client-side, matching admission.bed
// (Bed.code) against the covered bed_codes (same identifier space).
import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Paper, Stack, TextField, Typography, InputAdornment, MenuItem, LinearProgress } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { useNurseData } from "../NurseDataContext";
import { useMyCoverage } from "../hooks";
import { NURSE_COLORS } from "../theme";
import { PatientCard } from "../components";

export default function MyPatients() {
  const { patients, patientsLoading, reloadPatients, navigate } = useNurseData();
  const [q, setQ] = useState("");
  const [ward, setWard] = useState("All");
  // myBedCodes is null while the roster/assignment coverage is still loading
  // (so we don't flash "no patients"), a Set of covered bed_codes once ready.
  // It re-resolves on its own at each shift boundary — see useMyCoverage.
  const { myBedCodes } = useMyCoverage();

  // Debounced server-side search.
  useEffect(() => {
    const t = setTimeout(() => {
      reloadPatients({ search: q.trim() || undefined, ward: ward === "All" ? undefined : ward });
    }, 350);
    return () => clearTimeout(t);
  }, [q, ward, reloadPatients]);

  const myPatients = useMemo(
    () => (myBedCodes ? patients.filter((p) => p.bed && myBedCodes.has(p.bed)) : []),
    [patients, myBedCodes]
  );

  const wards = useMemo(
    () => ["All", ...Array.from(new Set(myPatients.map((p) => p.ward).filter(Boolean)))],
    [myPatients]
  );

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">My Patients</Typography>
        <Typography variant="body2" color="text.secondary">Patients in the wards you're rostered to for the shift in progress (plus any beds assigned to you for it).</Typography>
      </Box>
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField
            size="small"
            placeholder="Search by name, UHID or mobile"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
            sx={{ flex: 1 }}
          />
          {/* Options are bed locations ("Floor 1 - Room 103"), and the backend maps
              this filter's ?ward= param to bed__location — so it is labelled
              "Location". The real ward is a separate field (wardName). */}
          <TextField select size="small" label="Location" value={ward} onChange={(e) => setWard(e.target.value)} sx={{ minWidth: 180 }}>
            {wards.map((w) => (<MenuItem key={w} value={w}>{w}</MenuItem>))}
          </TextField>
        </Stack>
      </Paper>
      {(patientsLoading || myBedCodes === null) && <LinearProgress sx={{ mb: 2 }} />}
      <Grid container spacing={1.5}>
        {myPatients.map((p) => (
          <Grid item xs={12} sm={6} md={4} key={p.id}>
            <PatientCard patient={p} onClick={() => navigate("patient", { id: p.id })} />
          </Grid>
        ))}
        {!patientsLoading && myBedCodes !== null && myPatients.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {myBedCodes.size === 0
                  ? "You're not rostered to any ward for the shift in progress. Ask your nurse manager to roster you (or assign you a bed)."
                  : "No covered patients match the filter."}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
